import { PASSWORD_POLICY } from '@shared/constants';

/**
 * Individual requirement validators for granular feedback. The character types are Auth0's:
 * ASCII letters and digits, and its symbols (space and ASCII punctuation). Any other character is
 * allowed but counts towards no type.
 */
export const passwordRequirements = {
    minLength: (password: string) => password.length >= PASSWORD_POLICY.minLength,
    hasUppercase: (password: string) => /[A-Z]/.test(password),
    hasLowercase: (password: string) => /[a-z]/.test(password),
    hasNumber: (password: string) => /[0-9]/.test(password),
    hasSpecialChar: (password: string) => [...password].some((char) => PASSWORD_POLICY.symbols.includes(char)),
};

/** How many of the four character types (lowercase, uppercase, numbers, symbols) the password uses. */
export function countPasswordCharacterTypes(password: string) {
    return [
        passwordRequirements.hasLowercase,
        passwordRequirements.hasUppercase,
        passwordRequirements.hasNumber,
        passwordRequirements.hasSpecialChar,
    ].filter((hasType) => hasType(password)).length;
}

/**
 * Whether a new password meets the length and character-type rules of Auth0's policy: at least 8
 * characters and at least 3 of the 4 types. Auth0 still refuses common passwords and personal
 * information on submit, and an existing password is never checked against this.
 */
export function validatePassword(password: string) {
    return (
        passwordRequirements.minLength(password) &&
        countPasswordCharacterTypes(password) >= PASSWORD_POLICY.minCharacterTypes
    );
}

/**
 * Gets the first failing password requirement error message
 * Returns null if password meets all requirements or is empty
 */
export function getPasswordValidationError(password: string): string | null {
    if (!password) return null;
    if (!passwordRequirements.minLength(password)) return 'Use at least 8 characters';
    if (countPasswordCharacterTypes(password) < PASSWORD_POLICY.minCharacterTypes) {
        return 'Use at least 3 of the 4 character types';
    }
    return null;
}
